import {
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  pairwise,
  startWith,
  tap,
} from 'rxjs';
import { isEqual } from 'lodash';
import { APIOrganizationUser, ArdoqId, ViewIds } from '@ardoq/api-types';
import {
  type ReducerArray,
  action$,
  combineReducers,
  dispatchAction,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import type { PermissionContext } from '@ardoq/access-control';
import type { ContextSort } from '@ardoq/data-model';
import { viewSourceData$, type ViewSourceData } from 'tabview/viewSourceData$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { sort$ } from 'streams/context/sort$';
import { orgUsersById$ } from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import type { HasDataWorkToken } from 'tabview/activeViewsStatus/types';
import {
  createViewStatusReporter,
  registerWhileSubscribed,
  reportDataWorkStarted,
  withDataWork,
} from 'tabview/activeViewsStatus/viewStatusReporter';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { getFilteredViewSettingsPlain$ } from 'views/filteredViewSettings$';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { isPresentationMode } from 'env';
import {
  focusedComponentChanged,
  hoveredComponentChanged,
} from 'tabview/actions';
import {
  bypassLimitAction,
  componentMatrixCollapseAll,
  componentMatrixCollapseAllColumns,
  componentMatrixCollapseAllRows,
  componentMatrixExpandAll,
  componentMatrixExpandAllColumns,
  componentMatrixExpandAllRows,
  componentMatrixToggleCollapseGroup,
} from '../../actions';
import { getSanitizedComponentTypesToPersist } from '../utils';
import { getComponentMatrix2ViewComponentIds } from '../viewComponentIds';
import type {
  ComponentMatrix2Config,
  ComponentMatrix2ViewSettings,
  ViewpointModeComponentMatrixViewProperties,
  ViewpointModeComponentMatrixViewStreamState,
} from '../types';
import {
  buildComponentMatrix2ViewModel,
  getBypassLimitArgs,
  getExpandedStateToPersist,
  handleFocusedComponentChanged,
  handleHoveredComponentChanged,
  IGNORE_VIEW_SETTINGS,
  initialState,
  reduceCollapseAll,
  reduceCollapseAllColumns,
  reduceCollapseAllRows,
  reduceCollapseGroup,
  reduceExpandAll,
  reduceExpandAllColumns,
  reduceExpandAllRows,
  withCarriedBypassLimit,
} from './viewModelUtils';

/** Reducers for expanding and collapsing groups, hover and focus. Each returns a new state derived
 *  from the current view model, without building it from the loaded graph again. */
export const getRenderingDataReducers = <
  State extends ViewpointModeComponentMatrixViewStreamState,
>(): ReducerArray<State> => [
  reducer(componentMatrixToggleCollapseGroup, reduceCollapseGroup<State>),
  reducer(componentMatrixCollapseAll, reduceCollapseAll<State>),
  reducer(componentMatrixExpandAll, reduceExpandAll<State>),
  reducer(componentMatrixCollapseAllRows, reduceCollapseAllRows<State>),
  reducer(componentMatrixExpandAllRows, reduceExpandAllRows<State>),
  reducer(componentMatrixCollapseAllColumns, reduceCollapseAllColumns<State>),
  reducer(componentMatrixExpandAllColumns, reduceExpandAllColumns<State>),
  reducer(hoveredComponentChanged, handleHoveredComponentChanged<State>),
  reducer(focusedComponentChanged, handleFocusedComponentChanged<State>),
];

type ViewpointModeStreamStateWithDataWork =
  ViewpointModeComponentMatrixViewStreamState & {
    dataWorkToken: string | null;
  };

const initialStateWithDataWork: ViewpointModeStreamStateWithDataWork = {
  ...initialState,
  dataWorkToken: null,
};

const VIEW_ID = ViewIds.COMPONENT_MATRIX_2;

const viewStatusReporter = createViewStatusReporter(
  toViewpointModeMainViewInstanceId(VIEW_ID)
);

const dispatchBypassLimit = () => dispatchAction(bypassLimitAction());

type ResetArgs = [
  ComponentMatrix2ViewSettings,
  ContextSort,
  ViewSourceData,
  PermissionContext,
  Record<ArdoqId, APIOrganizationUser>,
];

const resetFromStream = (
  previousState: ViewpointModeStreamStateWithDataWork,
  {
    resetArgs: [
      viewSettings,
      sort,
      { loadedGraph, context, locale, i18nLocale, formattingAndLabelRules },
      permissionContext,
      orgUsersById,
    ],
    dataWorkToken,
  }: { resetArgs: ResetArgs } & HasDataWorkToken
): ViewpointModeStreamStateWithDataWork => ({
  ...buildComponentMatrix2ViewModel(
    withCarriedBypassLimit(previousState, {
      viewSettings,
      loadedGraph,
      onProceedAnyway: dispatchBypassLimit,
      i18nLocale,
      orgUsersById,
      sort,
      userLocale: locale,
      contextComponentId: context.componentId || null,
      permissionContext,
      formattingAndLabelRules: formattingAndLabelRules.rules,
    })
  ),
  dataWorkToken,
});

const resetReducer = streamReducer(
  combineLatest([
    getFilteredViewSettingsPlain$(
      getViewpointModeViewSettingsWithChanges$(
        VIEW_ID,
        toViewpointModeMainViewInstanceId(VIEW_ID)
      ),
      IGNORE_VIEW_SETTINGS
    ).pipe(
      map(({ currentSettings }) => currentSettings),
      distinctUntilChanged((a, b) => isEqual(a, b))
    ),
    sort$,
    viewSourceData$,
    currentUserPermissionContext$,
    orgUsersById$,
  ]).pipe(
    reportDataWorkStarted(viewStatusReporter, 'reset'),
    debounceTime(0),
    map(resetArgs => ({ resetArgs })),
    withDataWork(viewStatusReporter, 'source')
  ),
  resetFromStream
);

const handleBypassLimitFromStream = (
  state: ViewpointModeStreamStateWithDataWork
): ViewpointModeStreamStateWithDataWork => ({
  ...buildComponentMatrix2ViewModel(
    getBypassLimitArgs(state, dispatchBypassLimit)
  ),
  dataWorkToken: state.dataWorkToken,
});

const maybePersistExpandedState = (
  prevState: ViewpointModeComponentMatrixViewStreamState,
  state: ViewpointModeComponentMatrixViewStreamState
): void => {
  const expandedState = getExpandedStateToPersist(
    prevState.viewModel,
    state.viewModel
  );
  if (!expandedState) return;
  onViewSettingsUpdate(
    VIEW_ID,
    { persistedExpandedState: expandedState },
    true
  );
};

const maybePublishViewComponentIds = (
  prevState: ViewpointModeComponentMatrixViewStreamState,
  state: ViewpointModeComponentMatrixViewStreamState
): void => {
  if (
    prevState.viewModel.items === state.viewModel.items &&
    prevState.viewModel.rows === state.viewModel.rows &&
    prevState.viewModel.columns === state.viewModel.columns
  )
    return;
  dispatchAction(
    readViewComponentIds({
      [VIEW_ID]: getComponentMatrix2ViewComponentIds(state.viewModel),
    })
  );
};

const maybeClearStaleComponentTypes = (
  prevState: ViewpointModeComponentMatrixViewStreamState,
  state: ViewpointModeComponentMatrixViewStreamState
): void => {
  const sanitizeInputsUnchanged =
    prevState.viewSettings.selectedComponentTypes ===
      state.viewSettings.selectedComponentTypes &&
    prevState.viewSettingsState.availableComponentTypes ===
      state.viewSettingsState.availableComponentTypes;
  if (sanitizeInputsUnchanged) return;
  const sanitized = getSanitizedComponentTypesToPersist(
    state.viewSettings.selectedComponentTypes,
    state.viewSettingsState.availableComponentTypes
  );
  if (sanitized === null) return;
  onViewSettingsUpdate(VIEW_ID, { selectedComponentTypes: sanitized }, true);
};

const config: ComponentMatrix2Config = {
  isPresentationMode: isPresentationMode(),
};

export const viewpointModeViewModel$: Observable<
  Omit<ViewpointModeComponentMatrixViewProperties, 'commands'>
> = action$.pipe(
  combineReducers<ViewpointModeStreamStateWithDataWork>(
    initialStateWithDataWork,
    [
      ...getRenderingDataReducers<ViewpointModeStreamStateWithDataWork>(),
      resetReducer,
      reducer(bypassLimitAction, handleBypassLimitFromStream),
    ]
  ),
  startWith(initialStateWithDataWork),
  pairwise(),
  tap(([prevState, state]) => {
    maybePersistExpandedState(prevState, state);
    maybeClearStaleComponentTypes(prevState, state);
    maybePublishViewComponentIds(prevState, state);
  }),
  map(([, state]) => ({ ...state, config, viewStatusReporter })),
  registerWhileSubscribed(viewStatusReporter)
);
